import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import LazyLoad from "react-lazyload";
import tempImage from "../../assets/images/temp.jpg"; // Placeholder image
import CTASection from "../../components/CTASection"; // Adjust the path as necessary
import Accordion from "../../components/Accordion"; // Ensure the path is correct for Accordion component

const DonateACar = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What kinds of vehicles can be donated?",
      answer:
        "We accept almost all vehicles, including cars, trucks, motorcycles, recreational vehicles, boats, snowmobiles, farm equipment, and construction equipment. If you want to donate your car or truck, please go to our donation wizard. If you want to donate any other type of vehicle, please call us at 1-877-277-4344.",
    },
    {
      question:
        "When my vehicle is sold, how much of the money goes to Habitat?",
      answer:
        "Our partners at Advanced Remarketing Services return a higher percentage than any other vehicle processing entity. On a monthly basis, between 84% and 87% of the gross revenue generated from vehicle sales comes to Habitat for Humanity. The higher return helps make a more positive impact on communities across the country, which may be why Forbes has recognized Habitat for Humanity as one of the best charities to donate your car or other vehicle for two years in a row. From 2019-2023, your vehicle donations helped generate more than $72 million for affordable homes.",
    },
    {
      question: "What happens after my vehicle is picked up?",
      answer:
        "Your vehicle will be sold on behalf of Habitat through a national network of auctions and direct buyers. After the vehicle has been sold, you will receive an acknowledgment letter providing your vehicle sale information.",
    },
    {
      question:
        "How do I know my vehicle is in good hands once it’s been picked up?",
      answer:
        "All the auctions and direct buyers used by Habitat are licensed, insured, and bonded in the states where they operate. By signing the title directly to Advanced Remarketing Services, you are legally transferring ownership of your vehicle to Habitat for Humanity. When your title is received and your vehicle is picked up, you are no longer responsible for the vehicle.",
    },
    {
      question: "What happens to donated cars?",
      answer:
        "About half of our cars are sold to licensed dealers at wholesale auctions and are reused for transportation. The other half are typically transported to auto salvage yards where all reusable car parts, batteries, tires, and fluids are removed. Vehicles that can be recycled are crushed, shredded, and recycled into steel. Habitat receives funds for each donated vehicle, although a car sold at auction generally generates more revenue than one that is recycled. Every donation makes a difference and the families that partner with us are extremely grateful for your help.",
    },
    {
      question: "What is the environmental impact of donating my car?",
      answer:
        "Many of the vehicles donated to Cars for Homes to raise funds to help build homes are sold directly to licensed auto recyclers. These recyclers are crucial environmental stewards and help to ensure end-of-life vehicles are processed safely and in environmentally friendly ways. Recycling cars keeps a huge amount of steel out of landfills. According to the National Material Company, more than 80 million tons of steel are recycled each year in North America. For every ton of steel recycled, 2,500 pounds of iron ore, 1,400 pounds of coal, and 120 pounds of limestone are conserved. Cars recycled properly can play a big role in reducing carbon emissions in the automotive manufacturing industry because recycling reduces the need to refine new materials. Recycling steel also saves the equivalent energy to power about 18 million households for a year. Donating your vehicle to Cars for Homes is a win for the community and the environment.",
    },
    {
      question: "Who is Advanced Remarketing Services, Inc.?",
      answer:
        "Advanced Remarketing Services is our authorized agent and attorney-in-fact for the purpose of selling vehicles and transferring titles. They handle the title transfer, towing, and reporting of all vehicle donations. Vehicle donations may be made online through the donation wizard or by calling 1-877-277-4344.",
    },
    {
      question: "What information do I need to donate my car?",
      answer:
        "Here are some basic things you should be prepared to know and provide for the donation: Valid car title, free of liens, Year, make, and model, VIN, Body type, Mileage, Condition, Date the vehicle was last driven. This is not a complete list. For a full list of vehicle donation needs, please go to the donation wizard and start your donation.",
    },
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Donate A Car - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Donate your car to Habitat for Humanity Buffalo and help build homes for families in need. Learn about the donation process and how your contribution makes a difference."
        />
      </Helmet>
      <Container>
        <h1>Donate A Car</h1>
        <h2 className="section-header primary">
          That Clunker in Your Driveway Can Build a Home!
        </h2>
        <Section>
          <p>
            Did you know by donating your vehicle to Habitat Buffalo you can
            help build and repair affordable homes? Your vehicle donation is one
            more way that you can support our work in the Greater Buffalo area.
            Plus, it's fast and easy to do, and your donation will qualify for
            tax deductions. We accept cars, trucks, RV's, vans, SUV's, boats,
            and motorcycles whether they are running or not.
          </p>
        </Section>
        <Section>
          <IframeContainer>
            <iframe
              src="https://www.youtube.com/embed/KWF1t23yrpY"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </IframeContainer>
        </Section>
        <Section>
          <p>
            "We had the privilege of being selected as a Partner Family for
            Habitat Buffalo. At every build, I felt honored and humbled by the
            amazing people who came to work on our home. Today, I get to live in
            a home built by love. So, it is from a deep sense of gratitude that
            I give to Habitat for Humanity. Donating our car was a natural step
            to give back to the community that gave us so much."
          </p>
          <p>- Noel, Habitat Homeowner</p>
        </Section>

        <Section>
          <h2 className="section-header orange">
            Top Rated Vehicle Donation Program by Forbes!
          </h2>
          <p>
            We're proud that Forbes listed Habitat for Humanity as a top vehicle
            donation program in the United States! The list was based on the
            efficiency and impact of our vehicle donation program. This means
            each dollar from your vehicle donation will make an even greater
            impact right here in our community!
          </p>
        </Section>
        <CTASection
          title="Start Your Car Donation Now"
          description="Ready to make a difference? Start your car donation now and help build homes for families in need."
          buttonText="Donate Your Car"
          buttonLink="https://www.cardonationwizard.com/habitat-for-humanity/donate/donate-a-car-buil%E2%80%A6?__cr_user=ba21a69ccbe1c5c34bce5bf38f358ade&__cr_session=5ded53f6950d784b4f6cb533c6f7e71a"
        />
        <FAQSection>
          <h2>Frequently Asked Questions</h2>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              index={index}
              activeIndex={openIndex}
              question={faq.question}
              answer={faq.answer}
              toggleAccordion={toggleAccordion}
            />
          ))}
        </FAQSection>
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 40px;
  text-align: center;

  &:last-child {
    margin-bottom: 0;
  }
`;

const IframeContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  margin-bottom: 40px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const Button = styled.a`
  background-color: var(--primary-color);
  color: var(--secondary-color);
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  font-family: var(--font-title);
  border-radius: 5px;
  margin-top: 20px;
  text-decoration: none; /* Ensure it looks like a button */
  display: inline-block;

  &:hover {
    background-color: var(--accent-color);
  }
`;

const FAQSection = styled.div`
  margin-top: 40px;
`;

export default DonateACar;
